<script setup lang="ts">
import {ref, watch} from "vue";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import {AutoComplete, Button, Dialog, Label} from "@/Components/Library";
import {deleteMultipleGroupQuestionResponses} from "@/Services/group-question-responses";

const props = defineProps<{
    group: any;
    groupQuestionIds: string[];
}>();

const visible = defineModel<boolean>("visible", {required: true});
const emit = defineEmits<{
    deleted: [];
}>();

const confirm = useConfirm();
const toast = useToast();
const selectedUsers = ref<any[]>([]);
const deleting = ref(false);

const deleteResponses = async () => {
    try {
        deleting.value = true;
        const {count} = await deleteMultipleGroupQuestionResponses(props.group.id, {
            group_questions: props.groupQuestionIds,
            users: selectedUsers.value.map((user) => user.id),
        });

        toast.add({
            severity: "success",
            summary: "Responses deleted",
            detail: `${count} responses have been deleted.`,
            life: 3000,
        });
        visible.value = false;
        emit("deleted");
    } catch (error) {
        console.error(error);
    } finally {
        deleting.value = false;
    }
};

const confirmFinalDeletion = () => {
    confirm.require({
        header: "Final confirmation",
        message: "This action is destructive and cannot be undone. Delete these responses permanently?",
        acceptLabel: "Delete responses",
        acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: deleteResponses,
    });
};

const confirmDeletion = () => {
    confirm.require({
        header: "Delete responses",
        message: `Delete responses from ${selectedUsers.value.length} selected users across ${props.groupQuestionIds.length} selected posts?`,
        acceptLabel: "Continue",
        acceptClass: "bg-red-600 px-3 py-1.5 rounded-md text-white",
        rejectClass: "px-3 py-1.5",
        accept: () => setTimeout(confirmFinalDeletion),
    });
};

watch(visible, (isVisible) => {
    if (isVisible) {
        selectedUsers.value = [];
    }
});
</script>

<template>
    <Dialog
        v-model:visible="visible"
        modal
        dismissable-mask
        class="w-full max-w-lg overflow-hidden"
        :header="`Delete responses for ${groupQuestionIds.length} posts`"
    >
        <form class="flex flex-col gap-4" @submit.prevent="confirmDeletion">
            <div class="flex flex-col gap-1">
                <Label for="response-users" required>Group members</Label>
                <AutoComplete
                    input-id="response-users"
                    v-model="selectedUsers"
                    :items="[group.user, ...group.members]"
                    option-label="name"
                    placeholder="Select members"
                    multiple
                    dropdown
                    required
                />
            </div>
            <p class="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">
                Only responses to the selected posts will be deleted. You will be asked to confirm twice.
            </p>
            <Button
                type="submit"
                class="w-full bg-red-600 text-white hover:brightness-110"
                :disabled="!selectedUsers.length"
                :loading="deleting"
            >
                Delete responses
            </Button>
        </form>
    </Dialog>
</template>
